// Location: src/components/admin/schedule/ScheduleHeader.tsx
// Purpose: Header section of schedule card with type, status, and action buttons
// Scope: Display schedule type badge, status badge, and control buttons

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Id } from '@/convex/_generated/dataModel';
import { Schedule } from '@/types/schedules';

interface ScheduleHeaderProps {
	schedule: Schedule;
	isSuperAdmin: boolean;
	userOwnsSchedule: boolean;
	isTaskRunning: boolean;
	formatScheduleType: (type: string) => string;
	onDelete: (id: Id<'schedules'>, type: string) => void;
	onRunNow: (scheduleId: Id<'schedules'>) => void;
	onResetSchedule: () => void;
	runNowDisabledReason?: string;
}

export const ScheduleHeader = React.memo(function ScheduleHeader({
	schedule,
	isSuperAdmin,
	userOwnsSchedule,
	isTaskRunning,
	formatScheduleType,
	onDelete,
	onRunNow,
	onResetSchedule,
	runNowDisabledReason,
}: ScheduleHeaderProps) {
	return (
		<div className='flex items-center justify-between gap-4'>
			<div className='flex items-center gap-2'>
				<Badge
					className={cn(
						'text-white text-base px-4 py-2 border-0',
						schedule.type === 'pod_start' &&
							'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
						schedule.type === 'post_scraper' &&
							'bg-gradient-to-b from-pink-300 via-pink-500 to-pink-700',
						(schedule.type === 'profile_comment' ||
							schedule.type === 'comments') &&
							'bg-gradient-to-b from-blue-300 via-blue-500 to-blue-700'
					)}
				>
					<span className='inline-flex items-center'>
						{formatScheduleType(schedule.type)}
					</span>
				</Badge>
				<Badge
					className={cn(
						'text-base px-4 py-2',
						schedule.status === 'active'
							? 'bg-gradient-to-b from-green-400 via-green-600 to-green-900 text-white border-0 pointer-events-none'
							: 'bg-gradient-to-b from-neutral-400 via-neutral-600 to-neutral-900 text-white border-0'
					)}
				>
					<span className='inline-flex items-center gap-1'>
						{schedule.status === 'active' ? '🟢' : '⏸️'}
						<span>
							{schedule.status === 'active' ? 'ACTIVE' : 'PAUSED'}
						</span>
					</span>
				</Badge>
			</div>
			<div className='flex gap-2'>
				{isSuperAdmin && (
					<Button
						variant='destructive'
						size='sm'
						onClick={() =>
							onDelete(
								schedule._id,
								formatScheduleType(schedule.type)
							)
						}
						disabled={isTaskRunning}
						className={cn(
							isTaskRunning && 'opacity-30 cursor-not-allowed'
						)}
					>
						<span className='flex items-center gap-1'>
							🗑️ <span>Delete</span>
						</span>
					</Button>
				)}
				{/* 🔒 SECURITY: Show Reset button only when task is stuck running AND for Super Admin OR users who own the pod */}
				{isTaskRunning && (isSuperAdmin || userOwnsSchedule) && (
					<Button
						variant='outline'
						size='sm'
						onClick={onResetSchedule}
						className={cn(
							'bg-gradient-to-br from-orange-400 via-orange-500 to-red-600 hover:from-orange-500 hover:via-orange-600 hover:to-red-700 text-white border-none shadow-md hover:shadow-lg transition-all duration-200',
							'hover:scale-105 relative',
							'after:content-[""] after:absolute after:inset-0 after:z-[-1] after:rounded-md after:bg-orange-500/40 after:blur-sm after:opacity-70 after:transform after:scale-110'
						)}
					>
						<span className='flex items-center gap-1'>
							🔄 <span>Reset</span>
						</span>
					</Button>
				)}
				{/* 🔒 SECURITY: Show Run Now button only for Super Admin OR users who own the pod */}
				{(isSuperAdmin || userOwnsSchedule) && (
					<Button
						variant='outline'
						size='sm'
						onClick={() => onRunNow(schedule._id)}
						disabled={isTaskRunning || Boolean(runNowDisabledReason)}
						title={runNowDisabledReason}
						className={cn(
							'bg-gradient-to-br from-pink-400 via-pink-500 to-fuchsia-600 hover:from-pink-500 hover:via-pink-600 hover:to-fuchsia-700 text-white border-none shadow-md hover:shadow-lg transition-all duration-200',
							'hover:scale-105 relative',
							'after:content-[""] after:absolute after:inset-0 after:z-[-1] after:rounded-md after:bg-pink-500/40 after:blur-sm after:opacity-70 after:transform after:scale-110',
							(isTaskRunning || runNowDisabledReason) &&
								'opacity-50 cursor-not-allowed hover:scale-100'
						)}
					>
						<span className='flex items-center gap-1'>
							⚡️ <span>Run Now</span>
						</span>
					</Button>
				)}
			</div>
		</div>
	);
});
